type HslaColor = { h: number; s: number; l: number; a: number; ads?: string };
const BaseColors = [
  'red',
  'redDark',
  'blurpleLight',
  'blurpleMedium',
  'blurple',
  'blurpleDark',
  'blurpleStrong',
  'offWhite',
  'blueLight',
  'blue',
  'blueDark',
  'magentaLight',
  'magenta',
  'magentaDark',
  'orangeLight',
  'orange',
  'orangeDark',
  'purple',
  'purpleDark',
  'purpleLight',
  'tealLight',
  'teal',
  'tealDark',
  'yellowLight',
  'yellow',
  'yellowDark',
] as const;
const GreyColors = [
  'grey8',
  'grey7',
  'grey6',
  'grey5',
  'grey4',
  'grey3',
  'grey2',
  'grey1',
  'white',
] as const;
const SharedColors = [
  'record',
  'recordHover',
  'recordActive',
  'backdropDark',
  'backdropTwilight',
  'highlight',
  'highlightHover',
  'highlightActive',
  'warning',
] as const;
const SemanticColors = [
  'primary',
  'primaryHover',
  'primaryActive',
  'body',
  'bodyDimmed',
  'bodyInverse',
  'background',
  'backgroundHover',
  'backgroundActive',
  'backgroundNeutral',
  'backgroundNeutralHover',
  'backgroundNeutralActive',
  'backgroundSecondary',
  'backgroundSecondary2',
  'backgroundInverse',
  'focusRing',
  'overlay',
  'overlayHover',
  'overlayActive',
  'backdrop',
  'border',
  'disabledContent',
  'disabledBackground',
  'formFieldBorder',
  'formFieldBackground',
  'buttonBorder',
  'upgrade',
  'upgradeHover',
  'upgradeActive',
  'tabBackground',
  'discoveryBackground',
  'discoveryLightBackground',
  'discoveryTitle',
  'discoveryHighlight',
  'info',
  'success',
  'danger',
  'dangerHover',
  'dangerActive',
  /** Illustration accent: primary in light mode, blurpleDark in dark. Resolves via CSS inheritance. */
  'illustrationAccent',
  /** Illustration orange: yellowDark in light, darkOrange in dark. */
  'illustrationOrange',
  /** Illustration line: lineStroke in light, grayLine in dark. */
  'illustrationLine',
  /** Illustration purple: purpleLight in light, purpleDark in dark. */
  'illustrationPurple',
  /** Illustration green: greenLight in light, greenDark in dark. */
  'illustrationGreen',
  /** Illustration gray: grey3 in light, gray in dark. */
  'illustrationGray',
  /** Illustration accent gray: grey4 in light, formFieldBorder in dark. */
  'illustrationAccentGray',
  /** Illustration stroke: white in light, lineStroke in dark. */
  'illustrationStroke',
  /** Illustration drawer: white in light, formFieldBackground in dark. */
  'illustrationDrawer',
  /** Illustration paper: grey3 in light, grayLine in dark. */
  'illustrationPaper',
  /** Illustration inner paper: grey3 in light, bodyDimmed in dark. */
  'illustrationInnerPaper',
  /** Illustration green shade: greenDarkShadeLight in light, greenDarkShade in dark. */
  'illustrationGreenShade',
  /** Illustration blue dark: staticDarkBlue in light, blueDark in dark. */
  'illustrationBlueDark',
  /** Illustration face: white in light, face in dark. */
  'illustrationFace',
  /** Illustration rainstorm gray: rainstormGray in light, grayLine in dark. */
  'illustrationRainstormGray',
  /** Illustration check blue: primary in light, staticDarkBlue in dark. */
  'illustrationCheckBlue',
  /** Illustration greyscale fill: grayLine in light, gray in dark. */
  'illustrationGreyscaleFill',
  /** Illustration greyscale accent: grayMedium in light, grayDarkMid in dark. */
  'illustrationGreyscaleAccent',
  /** Illustration greyscale mid: grayMid in light, grayDarkMid in dark. */
  'illustrationGreyscaleMid',
  /** Illustration greyscale mid dark: grayMid in light, grayDark in dark. */
  'illustrationGreyscaleMidDark',
  /** Illustration greyscale dark: grayDark in light, lineStroke in dark. */
  'illustrationGreyscaleDark',
  /** Illustration greyscale stroke: lineStroke in light, grayLine in dark. */
  'illustrationGreyscaleStroke',
  /** Illustration bg: white in light, transparent in dark. */
  'illustrationBg',
] as const;

type ThemesColorVariant = Record<
  (typeof SharedColors)[number] | (typeof SemanticColors)[number],
  HslaColor
>;
type ThemesColors = {
  light: ThemesColorVariant;
  dark: ThemesColorVariant;
};

const percent = (number: number, percentage: number) =>
  Math.round((number * percentage) / 100);

export const darken = (color: HslaColor, percentage: number): HslaColor => ({
  ...color,
  l: color.l - percent(color.l, percentage),
});

export const lighten = (color: HslaColor, percentage: number): HslaColor => ({
  ...color,
  l: color.l + percent(color.l, percentage),
});

export const saturate = (color: HslaColor, percentage: number): HslaColor => ({
  ...color,
  s: color.s + percent(color.s, percentage),
});

export const alphaFunction = (color: HslaColor, alpha: number): HslaColor => ({
  ...color,
  a: alpha,
});

const hoverIncrease = 15.8;
const activeIncrease = 31.6;
const borderAlpha = 0.14;
const backdropAlpha = 0.46;
const backdropDarkAlpha = 0.9;
const backdropTwilightAlpha = 0.8;
const buttonBorderAlpha = 0.25;
const tabBackgroundAlpha = 0.06;
const highlightAlpha = 0.15;
const highlightHoverAlpha = 0.3;
const highlightActiveAlpha = 0.45;

export const hslaBaseColors: Record<(typeof BaseColors)[number], HslaColor> = {
  red: { h: 4, s: 64, l: 48, a: 1 },
  redDark: { h: 4, s: 56, l: 23, a: 1 },

  blurpleLight: { h: 214.3, s: 91.3, l: 95.5, a: 1 },
  blurpleMedium: { h: 216.5, s: 92, l: 90.2, a: 1 },
  blurple: { h: 215.4, s: 80, l: 47.65, a: 1 },
  blurpleDark: { h: 215.9, s: 79.9, l: 41, a: 1 },
  blurpleStrong: { h: 216.3, s: 69.2, l: 22.9, a: 1 },

  offWhite: { h: 0, s: 0, l: 97.25, a: 1 },

  blueLight: { h: 216.5, s: 92, l: 90.2, a: 1 },
  blue: { h: 215.4, s: 80, l: 47.65, a: 1 },
  blueDark: { h: 216.3, s: 69.2, l: 23, a: 1 },

  magentaLight: { h: 323, s: 92, l: 90, a: 1 },
  magenta: { h: 323, s: 42, l: 48, a: 1 },
  magentaDark: { h: 324, s: 37, l: 23, a: 1 },

  orangeLight: { h: 4, s: 100, l: 91.2, a: 1 },
  orange: { h: 11, s: 100, l: 62.2, a: 1 },
  orangeDark: { h: 10.9, s: 100, l: 42.2, a: 1 },

  purpleLight: { h: 277.3, s: 86.05, l: 91.57, a: 1 },
  purple: { h: 278.33, s: 85.71, l: 67.06, a: 1 },
  purpleDark: { h: 278.64, s: 48.36, l: 52.16, a: 1 },

  tealLight: { h: 155, s: 70, l: 84, a: 1 },
  teal: { h: 155, s: 62, l: 32, a: 1 },
  tealDark: { h: 155, s: 55, l: 19, a: 1 },

  yellowLight: { h: 43, s: 93, l: 82, a: 1 },
  yellow: { h: 45.5, s: 96, l: 57, a: 1 },
  yellowDark: { h: 39.8, s: 100, l: 49.4, a: 1 },
};

export const hslaGreys: Record<(typeof GreyColors)[number], HslaColor> = {
  grey8: { h: 228, s: 6, l: 17, a: 1 },
  grey7: { h: 223, s: 6, l: 24.5, a: 1 },
  grey6: { h: 224, s: 5, l: 44, a: 1 },
  grey5: { h: 224, s: 5, l: 57, a: 1 },
  grey4: { h: 223, s: 5, l: 73, a: 1 },
  grey3: { h: 225, s: 6, l: 87.5, a: 1 },
  grey2: { h: 210, s: 7, l: 94.5, a: 1 },
  grey1: { h: 0, s: 0, l: 97.25, a: 1 },
  white: { h: 0, s: 0, l: 100, a: 1 },
};

/** HSL equivalents of illustrationColors hex values for theme-aware semantic colors. */
const illustrationHsla: Record<string, HslaColor> = {
  darkOrange: { h: 27, s: 100, l: 45, a: 1 },
  gray: { h: 220, s: 4, l: 31, a: 1 },
  grayDark: { h: 228, s: 4, l: 25, a: 1 },
  grayDarkMid: { h: 228, s: 4, l: 51, a: 1 },
  grayMedium: { h: 228, s: 5, l: 73, a: 1 },
  grayMid: { h: 228, s: 5, l: 57, a: 1 },
  lineStroke: { h: 210, s: 11, l: 7, a: 1 },
  grayLine: { h: 210, s: 5, l: 89, a: 1 },
  purpleDark: { h: 279, s: 68, l: 61, a: 1 },
  purpleLight: { h: 279, s: 86, l: 67, a: 1 },
  greenDark: { h: 84, s: 63, l: 37, a: 1 },
  greenLight: { h: 84, s: 54, l: 46, a: 1 },
  greenDarkShade: { h: 84, s: 39, l: 20, a: 1 },
  greenDarkShadeLight: { h: 84, s: 56, l: 32, a: 1 },
  face: { h: 210, s: 6, l: 7, a: 1 },
  rainstormGray: { h: 220, s: 5, l: 12, a: 1 },
  staticDarkBlue: { h: 214, s: 85, l: 23, a: 1 },
};

const themeSharedColors: Record<(typeof SharedColors)[number], HslaColor> = {
  record: hslaBaseColors.orange,
  recordHover: darken(hslaBaseColors.orange, hoverIncrease),
  recordActive: darken(hslaBaseColors.orange, activeIncrease),
  backdropDark: alphaFunction(hslaGreys.grey8, backdropDarkAlpha),
  backdropTwilight: alphaFunction(
    hslaBaseColors.blurpleStrong,
    backdropTwilightAlpha
  ),
  highlight: alphaFunction(hslaBaseColors.blurple, highlightAlpha),
  highlightHover: alphaFunction(hslaBaseColors.blurple, highlightHoverAlpha),
  highlightActive: alphaFunction(hslaBaseColors.blurple, highlightActiveAlpha),
  warning: {
    h: 45.5,
    s: 96,
    l: 57,
    a: 1,
    ads: '--ds-background-warning-bold',
  },
};

// This object has a specific spread order for documentation purposes
export const themeColors: ThemesColors = {
  light: {
    primary: hslaBaseColors.blurple,
    primaryHover: hslaBaseColors.blurpleDark,
    primaryActive: hslaBaseColors.blurpleStrong,
    body: { h: 228, s: 6, l: 17, a: 1, ads: '--ds-text' },
    bodyDimmed: { h: 224, s: 5, l: 44, a: 1, ads: '--ds-text-subtlest' },
    bodyInverse: { h: 0, s: 0, l: 100, a: 1, ads: '--ds-text-inverse' },
    background: { h: 0, s: 0, l: 100, a: 1, ads: '--ds-surface' },
    backgroundHover: {
      h: 209,
      s: 75.6,
      l: 8,
      a: 0.08,
      ads: '--ds-background-neutral-subtle-hovered',
    },
    backgroundActive: {
      h: 225.5,
      s: 56.9,
      l: 10,
      a: 0.14,
      ads: '--ds-background-neutral-subtle-pressed',
    },
    backgroundSecondary: {
      h: 0,
      s: 0,
      l: 97.25,
      a: 1,
      ads: '--ds-surface-sunken',
    },
    backgroundSecondary2: {
      h: 0,
      s: 0,
      l: 97.25,
      a: 1,
      ads: '--ds-surface-sunken',
    },
    backgroundNeutral: {
      h: 209,
      s: 76,
      l: 8,
      a: 0.08,
      ads: '--ds-background-neutral',
    },
    backgroundNeutralHover: {
      h: 226,
      s: 57,
      l: 1,
      a: 0.14,
      ads: '--ds-background-neutral-hovered',
    },
    backgroundNeutralActive: {
      h: 223,
      s: 61,
      l: 8,
      a: 0.28,
      ads: '--ds-background-neutral-pressed',
    },
    backgroundInverse: {
      h: 228,
      s: 6,
      l: 17,
      a: 1,
      ads: '--ds-background-neutral-bold',
    },
    focusRing: {
      h: 216.1,
      s: 81.4,
      l: 60,
      a: 1,
      ads: '--ds-border-focused',
    },
    overlay: { h: 0, s: 0, l: 100, a: 1, ads: '--ds-surface-overlay' },
    overlayHover: hslaGreys.grey2,
    overlayActive: hslaGreys.grey3,
    backdrop: { h: 224, s: 72, l: 7, a: backdropAlpha, ads: '--ds-blanket' },
    border: { h: 225.5, s: 57, l: 10, a: borderAlpha, ads: '--ds-border' },
    ...themeSharedColors,
    info: {
      h: 215,
      s: 80.25,
      l: 47.65,
      a: 1,
      ads: '--ds-background-information-bold',
    },
    success: {
      h: 155,
      s: 62,
      l: 32,
      a: 1,
      ads: '--ds-background-accent-green-bolder',
    },
    danger: { h: 4, s: 64, l: 48, a: 1, ads: '--ds-background-danger-bold' },
    dangerHover: {
      h: 4.3,
      s: 65.7,
      l: 41.2,
      a: 1,
      ads: '--ds-background-danger-bold-hovered',
    },
    dangerActive: {
      h: 4.5,
      s: 56.3,
      l: 23.3,
      a: 1,
      ads: '--ds-background-danger-bold-pressed',
    },
    disabledContent: {
      h: 223,
      s: 5,
      l: 73,
      a: 1,
      ads: '--ds-text-disabled',
    },
    disabledBackground: {
      h: 0,
      s: 0,
      l: 9,
      a: 0.03,
      ads: '--ds-background-disabled',
    },
    formFieldBorder: { h: 223.6, s: 5, l: 57, a: 1, ads: '--ds-border-input' },
    formFieldBackground: {
      h: 0,
      s: 0,
      l: 100,
      a: 1,
      ads: '--ds-background-input',
    },
    buttonBorder: {
      h: 252,
      s: 13,
      l: 46,
      a: buttonBorderAlpha,
      ads: '--ds-border',
    },
    tabBackground: {
      h: 209,
      s: 75.6,
      l: 8,
      a: tabBackgroundAlpha,
      ads: '--ds-background-neutral',
    },
    upgrade: {
      h: 277.5,
      s: 89,
      l: 96.5,
      a: 1,
      ads: '--ds-background-discovery',
    },
    upgradeHover: hslaBaseColors.purpleLight,
    upgradeActive: {
      h: 278.6,
      s: 84.5,
      l: 79.8,
      a: 1,
      ads: '--ds-background-discovery-pressed',
    },
    discoveryBackground: hslaBaseColors.purpleDark,
    discoveryLightBackground: {
      h: 277.5,
      s: 89,
      l: 96.5,
      a: 1,
      ads: '--ds-background-discovery',
    },
    discoveryTitle: { h: 228, s: 6, l: 17, a: 1, ads: '--ds-text' },
    discoveryHighlight: {
      h: 277.5,
      s: 89,
      l: 96.5,
      a: 1,
      ads: '--ds-background-discovery',
    },
    illustrationAccent: hslaBaseColors.blurple,
    illustrationOrange: hslaBaseColors.yellowDark,
    illustrationLine: illustrationHsla.lineStroke,
    illustrationPurple: illustrationHsla.purpleLight,
    illustrationGreen: illustrationHsla.greenLight,
    illustrationGray: hslaGreys.grey3,
    illustrationAccentGray: hslaGreys.grey4,
    illustrationStroke: hslaGreys.white,
    illustrationDrawer: hslaGreys.white,
    illustrationPaper: hslaGreys.grey3,
    illustrationInnerPaper: hslaGreys.grey3,
    illustrationGreenShade: illustrationHsla.greenDarkShadeLight,
    illustrationBlueDark: illustrationHsla.staticDarkBlue,
    illustrationFace: hslaGreys.white,
    illustrationRainstormGray: illustrationHsla.rainstormGray,
    illustrationCheckBlue: hslaBaseColors.blurple,
    illustrationGreyscaleFill: illustrationHsla.grayLine,
    illustrationGreyscaleAccent: illustrationHsla.grayMedium,
    illustrationGreyscaleMid: illustrationHsla.grayMid,
    illustrationGreyscaleMidDark: illustrationHsla.grayMid,
    illustrationGreyscaleDark: illustrationHsla.grayDark,
    illustrationGreyscaleStroke: illustrationHsla.lineStroke,
    illustrationBg: hslaGreys.white,
  },

  dark: {
    primary: { h: 216.3, s: 83.2, l: 67.3, a: 1 },
    primaryHover: { h: 216.1, s: 85.1, l: 76.3, a: 1 },
    primaryActive: { h: 216.5, s: 92, l: 90.2, a: 1 },
    body: { h: 225, s: 4.3, l: 81.6, a: 1, ads: '--ds-text' },
    bodyDimmed: { h: 217.5, s: 4, l: 60.4, a: 1, ads: '--ds-text-subtlest' },
    bodyInverse: { h: 240, s: 3, l: 12.5, a: 1, ads: '--ds-text-inverse' },
    background: { h: 240, s: 3, l: 12.5, a: 1, ads: '--ds-surface' },
    backgroundHover: {
      h: 240,
      s: 12.6,
      l: 83,
      a: 0.07,
      ads: '--ds-background-neutral-subtle-hovered',
    },
    backgroundActive: {
      h: 236,
      s: 36.6,
      l: 92,
      a: 0.12,
      ads: '--ds-background-neutral-subtle-pressed',
    },
    backgroundSecondary: {
      h: 210,
      s: 4,
      l: 9.8,
      a: 1,
      ads: '--ds-surface-sunken',
    },
    backgroundSecondary2: {
      h: 210,
      s: 4,
      l: 9.8,
      a: 1,
      ads: '--ds-surface-sunken',
    },
    // Same base hsl to backgroundHover
    backgroundNeutral: {
      h: 240,
      s: 12.6,
      l: 83,
      a: 0.07,
      ads: '--ds-background-neutral',
    },
    backgroundNeutralHover: {
      h: 236,
      s: 36.6,
      l: 92,
      a: 0.12,
      ads: '--ds-background-neutral-hovered',
    },
    backgroundNeutralActive: {
      h: 226,
      s: 49,
      l: 93,
      a: 0.25,
      ads: '--ds-background-neutral-pressed',
    },
    backgroundInverse: {
      h: 225,
      s: 4.3,
      l: 81.6,
      a: 1,
      ads: '--ds-background-neutral-bold',
    },
    focusRing: {
      h: 216.1,
      s: 85.1,
      l: 76.3,
      a: 1,
      ads: '--ds-background-accent-blue-subtle-hovered',
    },
    overlay: { h: 225, s: 4, l: 17.6, a: 1, ads: '--ds-surface-overlay' },
    overlayHover: {
      h: 225,
      s: 4,
      l: 19.61,
      a: 1,
      ads: '--ds-surface-overlay-hovered',
    },
    overlayActive: {
      h: 225,
      s: 4.69,
      l: 25.1,
      a: 1,
      ads: '--ds-surface-overlay-pressed',
    },
    backdrop: { h: 210, s: 11, l: 7, a: 0.6, ads: '--ds-blanket' },
    border: { h: 236, s: 36.6, l: 92, a: 0.12, ads: '--ds-border' },
    ...themeSharedColors,
    info: {
      h: 216.3,
      s: 83,
      l: 67.25,
      a: 1,
      ads: '--ds-background-information-bold',
    },
    success: {
      h: 155,
      s: 57,
      l: 55,
      a: 1,
      ads: '--ds-background-accent-green-bolder',
    },
    danger: { h: 3.75, s: 91, l: 69, a: 1, ads: '--ds-background-danger-bold' },
    dangerHover: {
      h: 4,
      s: 96,
      l: 78,
      a: 1,
      ads: '--ds-background-danger-bold-hovered',
    },
    dangerActive: {
      h: 4,
      s: 100,
      l: 91.2,
      a: 1,
      ads: '--ds-background-danger-bold-pressed',
    },
    disabledContent: {
      h: 225,
      s: 5,
      l: 33,
      a: 1,
      ads: '--ds-text-disabled',
    },
    disabledBackground: {
      h: 0,
      s: 0,
      l: 1,
      a: 0.46,
      ads: '--ds-background-disabled',
    },
    formFieldBorder: { h: 222, s: 4, l: 51.4, a: 1, ads: '--ds-border-input' },
    formFieldBackground: {
      h: 225,
      s: 5,
      l: 15,
      a: 1,
      ads: '--ds-background-input',
    },
    buttonBorder: {
      h: 0,
      s: 0,
      l: 100,
      a: buttonBorderAlpha,
      ads: '--ds-border',
    },
    tabBackground: {
      h: 240,
      s: 12.6,
      l: 83,
      a: 0.07,
      ads: '--ds-background-neutral',
    },
    upgrade: {
      h: 277.8,
      s: 27.3,
      l: 19.4,
      a: 1,
      ads: '--ds-background-discovery',
    },
    upgradeHover: {
      h: 278,
      s: 44.2,
      l: 25.3,
      a: 1,
      ads: '--ds-background-discovery-hovered',
    },
    upgradeActive: {
      h: 278,
      s: 45,
      l: 44.7,
      a: 1,
      ads: '--ds-background-discovery-pressed',
    },
    discoveryBackground: {
      h: 278.5,
      s: 84.5,
      l: 72.2,
      a: 1,
      ads: '--ds-background-discovery-bold',
    },
    discoveryLightBackground: {
      h: 277.8,
      s: 27.3,
      l: 19.4,
      a: 1,
      ads: '--ds-background-discovery',
    },
    discoveryTitle: { h: 225, s: 4.3, l: 81.6, a: 1, ads: '--ds-text' },
    discoveryHighlight: {
      h: 277.8,
      s: 27.3,
      l: 19.4,
      a: 1,
      ads: '--ds-background-discovery',
    },
    illustrationAccent: hslaBaseColors.blurpleDark,
    illustrationOrange: illustrationHsla.darkOrange,
    illustrationLine: illustrationHsla.grayLine,
    illustrationPurple: illustrationHsla.purpleDark,
    illustrationGreen: illustrationHsla.greenDark,
    illustrationGray: illustrationHsla.gray,
    illustrationAccentGray: { h: 222, s: 4, l: 51.4, a: 1 }, // formFieldBorder
    illustrationStroke: illustrationHsla.lineStroke,
    illustrationDrawer: {
      h: 225,
      s: 5,
      l: 15,
      a: 1,
    }, // formFieldBackground
    illustrationPaper: illustrationHsla.grayLine,
    illustrationInnerPaper: { h: 217.5, s: 4, l: 60.4, a: 1 }, // bodyDimmed
    illustrationGreenShade: illustrationHsla.greenDarkShade,
    illustrationBlueDark: hslaBaseColors.blueDark,
    illustrationFace: illustrationHsla.face,
    illustrationRainstormGray: illustrationHsla.grayLine,
    illustrationCheckBlue: illustrationHsla.staticDarkBlue,
    illustrationGreyscaleFill: illustrationHsla.gray,
    illustrationGreyscaleAccent: illustrationHsla.grayDarkMid,
    illustrationGreyscaleMid: illustrationHsla.grayDarkMid,
    illustrationGreyscaleMidDark: illustrationHsla.grayDark,
    illustrationGreyscaleDark: illustrationHsla.lineStroke,
    illustrationGreyscaleStroke: illustrationHsla.grayLine,
    illustrationBg: { h: 0, s: 0, l: 0, a: 0 },
  },
};

export const illustrationColors = {
  staticDarkBlue: '#09326c',
  staticRed: '#ae2a19',
};

export const hslaColors = {
  ...hslaBaseColors,
  ...hslaGreys,
};

export const colorsNames = [
  ...Object.keys(hslaColors),
  ...Object.keys(themeColors.light),
];
